/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ReactNode } from 'react';
import Text from '@/shared/ui/components/basic/Text';
import UnstyledButton from '@/shared/ui/components/basic/UnstyledButton';
import { blockMidPadding } from './settings-block.styles';

/** The share of a quota above which the bar starts asking for attention. */
export const QUOTA_PRESSURE_RATIO = 0.9;

/** One metered limit of the subscribed plan. */
export interface PlanQuota {
  /** Stable key, also the testid suffix. */
  key: string;
  /** Already-localized "used of total" reading ("5 de 10"). */
  countLabel?: string;
  /** Already-localized name of what is metered. */
  label: string;
  /** Units consumed in the current period. */
  used: number;
  /**
   * Units the plan allows. Null means the plan reports no ceiling for this
   * quota, and a bar with no denominator is a bar that cannot be read — the
   * row then reports the count alone, without a track.
   */
  total: number | null;
  /** Already-localized line under the bar ("restam 7 de 150"). */
  caption?: string;
}

/**
 * The consumed share of a quota, clamped to the track.
 *
 * @param quota - The quota to measure.
 * @returns A ratio in [0, 1], or null when the quota has no ceiling.
 */
export function quotaRatio(quota: PlanQuota): number | null {
  if (quota.total === null || quota.total <= 0) return null;
  return Math.min(quota.used / quota.total, 1);
}

/**
 * Whether a quota is close enough to its ceiling to be worth flagging.
 *
 * @param quota - The quota to test.
 * @returns True when consumption is at or above {@link QUOTA_PRESSURE_RATIO}.
 */
export function isQuotaUnderPressure(quota: PlanQuota): boolean {
  const ratio = quotaRatio(quota);
  return ratio !== null && ratio >= QUOTA_PRESSURE_RATIO;
}

/**
 * Renders one quota: name, `used / total`, the bar, and what is left.
 *
 * @param props - Component props.
 * @param props.quota - The quota to render.
 * @returns The quota row element.
 */
function QuotaRow({ quota }: { quota: PlanQuota }) {
  const ratio = quotaRatio(quota);
  const pressured = isQuotaUnderPressure(quota);
  // Teal while there is room, copper once there is not. The wine seed is the
  // brand's own colour and reads as "this is the product" rather than as a
  // reading; a quota bar is a measurement and wants a measurement's tone.
  const tone = pressured ? 'var(--accent-cobre)' : 'var(--accent-petroleo)';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }} data-testid={`plan-quota-${quota.key}`}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <Text component="span" style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {quota.label}
        </Text>
        <Text
          component="span"
          style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--text-meta)', color: tone }}
        >
          {quota.countLabel ?? quota.used}
        </Text>
      </div>
      {ratio !== null && (
        <div style={{ height: 6, borderRadius: 3, background: 'var(--surface-card)', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${ratio * 100}%`, background: tone, borderRadius: 3 }} />
        </div>
      )}
      {quota.caption && (
        <Text
          component="span"
          style={{
            fontSize: 'var(--text-meta)',
            color: pressured ? 'var(--accent-cobre)' : 'var(--text-tertiary-soft)',
          }}
        >
          {quota.caption}
        </Text>
      )}
    </div>
  );
}

export interface PlanUsageBlockProps {
  /** The plan's headline price, already formatted ("R$ 349"). */
  price?: string;
  /** What the price is per, already localized ("/mês"). */
  pricePeriod?: string;
  /**
   * How the subscription is paid and when it renews next, in one already
   * localized line ("Cartão final 4412 · próxima cobrança em 12/10/2026").
   */
  billingLine?: string;
  /** Already-localized label of the link that changes the payment method. */
  billingActionLabel?: string;
  onChangeBilling?: () => void;
  /** Anything extra under the billing line — a banner, an error. */
  billingSlot?: ReactNode;
  /** The metered limits, in display order. */
  quotas: PlanQuota[];
}

/**
 * The `plan` mid: what the subscription costs, how it is paid, and how much
 * of it is spent.
 *
 * Two columns rather than a stack. The money and the consumption are two
 * different questions — "what am I paying" and "am I running out" — and
 * side by side each is answered at a glance instead of by scrolling past
 * the other.
 *
 * Only quotas the catalogue actually meters appear. The screen reports
 * consumption it can measure and stays quiet about the rest; a bar drawn
 * against a made-up ceiling is worse than no bar.
 *
 * @param props - Component props.
 * @returns The plan usage mid.
 */
export function PlanUsageBlock({
  price, pricePeriod, billingLine, billingActionLabel, onChangeBilling, billingSlot, quotas,
}: PlanUsageBlockProps) {
  return (
    // Exactly two tracks above the breakpoint, one below. An `auto-fit`
    // track list opened four of them on a wide block and left half the row
    // empty, because this mid has only ever two children.
    <div
      className="grid grid-cols-1 gap-(--space-5) md:grid-cols-2"
      style={{ padding: blockMidPadding }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minWidth: 0 }}>
        {price && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <Text
              component="span"
              data-testid="plan-price"
              style={{
                fontSize: '2.25rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1,
                color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums',
              }}
            >
              {price}
            </Text>
            {pricePeriod && (
              <Text
                component="span"
                style={{ fontSize: 'var(--text-body-sm)', fontWeight: 600, color: 'var(--text-tertiary-soft)' }}
              >
                {pricePeriod}
              </Text>
            )}
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
          {billingLine && (
            <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
              {billingLine}
            </Text>
          )}
          {onChangeBilling && billingActionLabel && (
            <UnstyledButton
              type="button"
              onClick={onChangeBilling}
              data-testid="change-payment-btn"
              style={{
                alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
                fontSize: 'var(--text-meta)', fontWeight: 600, color: 'var(--seed-base)',
                textDecoration: 'underline', textUnderlineOffset: '3px', cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              {billingActionLabel}
            </UnstyledButton>
          )}
          {billingSlot}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', minWidth: 0 }}>
        {quotas.map((quota) => <QuotaRow key={quota.key} quota={quota} />)}
      </div>
    </div>
  );
}
